// filepath: apps/web/src/features/admin/effects/HttpEndpointsPage.tsx
//
// FR-UI-ADM-8 -- main page for the HTTP endpoint admin editor.
//
// INV-5 (central): `auth_secret_value` lives only in
// `successAuthSecretValue` state and is cleared on dialog close.

import React, { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import {
  useCreateHttpEndpoint,
  useDeleteHttpEndpoint,
  useHttpEndpoints,
  usePatchHttpEndpoint,
} from "./queries";
import { HttpEndpointsTable } from "./HttpEndpointsTable";
import { HttpEndpointFormDialog } from "./HttpEndpointFormDialog";
import { HttpEndpointConflictDialog } from "./HttpEndpointConflictDialog";
import { PendingReviewBanner } from "./PendingReviewBanner";
import type { CreateHttpEndpointRequest, VersionConflictError } from "./types";

interface HttpEndpointsPageProps {
  environmentKind: string;
}

export function HttpEndpointsPage({
  environmentKind: _environmentKind,
}: HttpEndpointsPageProps): React.ReactElement {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useHttpEndpoints();
  const createMutation = useCreateHttpEndpoint();
  const patchMutation = usePatchHttpEndpoint();
  const deleteMutation = useDeleteHttpEndpoint();

  const [createOpen, setCreateOpen] = useState(false);
  const [successAuthSecretValue, setSuccessAuthSecretValue] = useState<
    string | null
  >(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<VersionConflictError | null>(null);

  const isPending =
    createMutation.isPending ||
    patchMutation.isPending ||
    deleteMutation.isPending;

  const handleCreate = async (req: CreateHttpEndpointRequest) => {
    setFormError(null);
    const resp = await createMutation.mutateAsync(req);
    setSuccessAuthSecretValue(resp.auth_secret_value);
  };

  const handleDisable = async (id: string, version: number) => {
    try {
      await patchMutation.mutateAsync({ id, status: "disabled", version });
    } catch (err) {
      if (err && typeof err === "object" && "code" in err) {
        setConflict(err as VersionConflictError);
      }
    }
  };

  const handleDelete = async (id: string, version: number) => {
    try {
      await deleteMutation.mutateAsync({ id, version });
    } catch (err) {
      if (err && typeof err === "object" && "code" in err) {
        setConflict(err as VersionConflictError);
      }
    }
  };

  if (isLoading) {
    return (
      <div data-testid="http-endpoints-loading">{t("common.loading")}</div>
    );
  }
  if (isError) {
    return (
      <div role="alert" data-testid="http-endpoints-error">
        {t("common.fetch_error")}
      </div>
    );
  }

  const endpoints = data?.endpoints ?? [];

  return (
    <div className="space-y-4 p-4" data-testid="http-endpoints-page">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">
          {t("admin.effects.http.page.title")}
        </h1>
        <button
          type="button"
          className="rounded bg-primary px-4 py-2 text-sm text-primary-foreground"
          onClick={() => setCreateOpen(true)}
          data-testid="http-endpoint-create-button"
        >
          {t("admin.effects.http.action.create")}
        </button>
      </div>

      <PendingReviewBanner defType="http_endpoint" />

      <HttpEndpointsTable
        endpoints={endpoints}
        onDisable={handleDisable}
        onDelete={handleDelete}
        isPending={isPending}
      />

      <HttpEndpointFormDialog
        open={createOpen}
        onSubmit={handleCreate}
        onClose={() => {
          setCreateOpen(false);
          setSuccessAuthSecretValue(null);
          setFormError(null);
        }}
        isPending={createMutation.isPending}
        successAuthSecretValue={successAuthSecretValue}
        error={formError}
      />

      <HttpEndpointConflictDialog
        open={conflict !== null}
        conflict={conflict}
        onRefetch={() => void 0}
        onDiscard={() => setConflict(null)}
        onClose={() => setConflict(null)}
      />
    </div>
  );
}
